import { useEffect, useRef, useState, useCallback } from 'react';

// ponytail: pdf.worker from CDN — bundle locally when offline deploy needed
const WORKER_SRC = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
// spread (two pages side by side) only on wide screens; portrait/small = single page
const SPREAD_MIN = 760;

const BOOK_CSS = `
.book3d { perspective: 1800px; }
.sheet { transform-style: preserve-3d; }
@keyframes turnOutFwd { from { transform: rotateY(0deg); } to { transform: rotateY(-100deg); } }
@keyframes turnInFwd  { from { transform: rotateY(100deg); } to { transform: rotateY(0deg); } }
@keyframes turnOutBwd { from { transform: rotateY(0deg); } to { transform: rotateY(100deg); } }
@keyframes turnInBwd  { from { transform: rotateY(-100deg); } to { transform: rotateY(0deg); } }
.sheet.flip-out-fwd { animation: turnOutFwd .4s ease forwards; }
.sheet.flip-in-fwd  { animation: turnInFwd  .4s ease forwards; }
.sheet.flip-out-bwd { animation: turnOutBwd .4s ease forwards; }
.sheet.flip-in-bwd  { animation: turnInBwd  .4s ease forwards; }
.bv-canvas { display: block; }
.bv-zoom-wrap { transform-origin: top center; transition: transform .15s ease; }
`;

export default function BookViewer({ url }) {
  const bookRef = useRef(null);
  const leftRef = useRef(null);
  const rightRef = useRef(null);
  const singleRef = useRef(null);
  const pdfRef = useRef(null);
  const pageRef = useRef(1);
  const timerRef = useRef(null);

  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [isSpread, setIsSpread] = useState(typeof window !== 'undefined' && window.innerWidth >= SPREAD_MIN);
  const [flip, setFlip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [zoom, setZoom] = useState(1.0);

  const zoomIn    = () => setZoom(z => Math.min(3.0, +(z + 0.25).toFixed(2)));
  const zoomOut   = () => setZoom(z => Math.max(0.5, +(z - 0.25).toFixed(2)));
  const zoomReset = () => setZoom(1.0);

  // load pdf once
  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    (async () => {
      try {
        const pdfjsLib = await import('pdfjs-dist');
        pdfjsLib.GlobalWorkerOptions.workerSrc = WORKER_SRC;
        const doc = await pdfjsLib.getDocument(url).promise;
        if (cancelled) return;
        pdfRef.current = { doc, runningL: null, runningR: null };
        setTotal(doc.numPages);
        setPage(1); pageRef.current = 1;
      } catch { if (!cancelled) setError('Gagal memuat PDF'); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; clearTimeout(timerRef.current); pdfRef.current = null; };
  }, [url]);

  // responsive
  useEffect(() => {
    const onResize = () => setIsSpread(window.innerWidth >= SPREAD_MIN);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // fullscreen state tracking
  useEffect(() => {
    const onFs = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  // render pages
  useEffect(() => {
    const ref = pdfRef.current;
    if (!ref) return;
    const { doc } = ref;

    const render = async (canvas, num, key) => {
      if (ref[key]) { ref[key].cancel(); ref[key] = null; }
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (num < 1 || num > total) {
        canvas.width = 1; canvas.height = 1;
        ctx.clearRect(0, 0, 1, 1);
        return;
      }
      try {
        const pdfPage = await doc.getPage(num);
        const dpr = window.devicePixelRatio || 1;
        const containerW = canvas.parentElement?.clientWidth || window.innerWidth;
        const baseVp = pdfPage.getViewport({ scale: 1 });
        const fitScale = (containerW / baseVp.width) * dpr;
        const vp = pdfPage.getViewport({ scale: fitScale });
        canvas.width = vp.width;
        canvas.height = vp.height;
        canvas.style.width  = (vp.width  / dpr) + 'px';
        canvas.style.height = (vp.height / dpr) + 'px';
        const task = pdfPage.render({ canvasContext: ctx, viewport: vp });
        ref[key] = task;
        await task.promise;
        ref[key] = null;
      } catch (e) {
        if (e.name !== 'RenderingCancelledException') ref[key] = null;
      }
    };

    if (isSpread) {
      render(leftRef.current, page, 'runningL');
      render(rightRef.current, page + 1, 'runningR');
    } else {
      render(singleRef.current, page, 'runningL');
      if (ref.runningR) { ref.runningR.cancel(); ref.runningR = null; }
    }
  }, [page, isSpread, total]);

  const finishFlip = useCallback((next, dir) => {
    setFlip(`in-${dir}`);
    timerRef.current = setTimeout(() => { setFlip(null); }, 420);
    setPage(next); pageRef.current = next;
  }, []);

  const go = useCallback((delta) => {
    const next = Math.max(1, Math.min(total, pageRef.current + delta));
    if (next === pageRef.current) return;
    const dir = delta > 0 ? 'fwd' : 'bwd';
    setFlip(`out-${dir}`);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => finishFlip(next, dir), 400);
  }, [total, finishFlip]);

  // touch swipe
  const touchX = useRef(null);
  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (dx < -40) go(1);
    else if (dx > 40) go(-1);
    touchX.current = null;
  };

  // keyboard
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'ArrowLeft') go(-1);
      else if (e.key === '+' || e.key === '=') zoomIn();
      else if (e.key === '-') zoomOut();
      else if (e.key === '0') zoomReset();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleFullscreen = () => {
    const el = bookRef.current;
    if (!el) return;
    if (document.fullscreenElement) document.exitFullscreen();
    else el.requestFullscreen?.();
  };

  if (!url || error) return error ? <p className="text-xs text-red-500 p-3">{error}</p> : null;

  const btn = "text-xs md:text-sm bg-white/85 hover:bg-white text-gray-700 px-3 py-1.5 rounded-lg shadow disabled:opacity-30 disabled:cursor-not-allowed transition select-none";
  const spread = isSpread;
  const maxP = spread ? Math.max(1, total - 1) : total;
  const sheetCls = `sheet ${flip || ''}`.trim();

  return (
    <div className="rounded-xl bg-gradient-to-br from-amber-50 via-orange-50 to-amber-100 shadow-inner overflow-hidden flex flex-col h-full">
      <style>{BOOK_CSS}</style>
      <div ref={bookRef} className="book3d flex flex-col flex-1 min-h-0">
        {/* toolbar */}
        <div className="flex items-center justify-between gap-1 px-2 py-2 bg-white/40 flex-shrink-0">
          <button className={btn} onClick={() => go(-1)} disabled={page <= 1} aria-label="Halaman sebelumnya">‹ Sebelum</button>
          <span className="text-[11px] md:text-xs text-gray-600 font-medium whitespace-nowrap">
            {loading ? 'Memuat…' : `Halaman ${page}${spread && page + 1 <= total ? '–' + Math.min(page + 1, total) : ''} dari ${total}`}
          </span>
          <div className="flex items-center gap-1">
            {/* zoom controls */}
            <button className={btn} onClick={zoomOut} disabled={zoom <= 0.5} aria-label="Perkecil">−</button>
            <button className={`${btn} min-w-[3rem] text-center`} onClick={zoomReset} aria-label="Reset zoom">
              {Math.round(zoom * 100)}%
            </button>
            <button className={btn} onClick={zoomIn} disabled={zoom >= 3.0} aria-label="Perbesar">+</button>
            <button className={btn} onClick={toggleFullscreen} aria-label={fullscreen ? 'Keluar layar penuh' : 'Layar penuh'}>
              {fullscreen ? '⤢' : '⤡'}
            </button>
            <button className={btn} onClick={() => go(1)} disabled={page >= maxP} aria-label="Halaman berikutnya">Berikutnya ›</button>
          </div>
        </div>

        {/* book body — scrollable when zoomed */}
        <div
          className="relative flex-1 min-h-0 overflow-auto px-2 md:px-4 pb-3 select-none"
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <div className="bv-zoom-wrap" style={{ transform: `scale(${zoom})`, transformOrigin: 'top center' }}>
            {spread ? (
              <div className={`relative w-full ${sheetCls}`}>
                <div className="flex relative">
                  <div className="relative flex-1">
                    <div className="absolute -left-1 top-1 bottom-1 w-2.5 rounded-l-lg bg-gradient-to-b from-gray-300 via-gray-200 to-gray-300 shadow-[inset_0_0_4px_rgba(0,0,0,0.18)]" />
                    <div className="bg-white rounded-l-lg overflow-hidden shadow-lg min-h-[240px] flex items-center justify-center">
                      <canvas ref={leftRef} className="bv-canvas" />
                    </div>
                  </div>
                  <div className="w-[3px] bg-gradient-to-b from-transparent via-gray-500/35 to-transparent shrink-0 z-10" />
                  <div className="relative flex-1">
                    <div className="absolute -right-1 top-1 bottom-1 w-2.5 rounded-r-lg bg-gradient-to-b from-gray-300 via-gray-200 to-gray-300 shadow-[inset_0_0_4px_rgba(0,0,0,0.18)]" />
                    <div className="bg-white rounded-r-lg overflow-hidden shadow-lg min-h-[240px] flex items-center justify-center">
                      <canvas ref={rightRef} className="bv-canvas" />
                    </div>
                  </div>
                </div>
                <div className="absolute top-0 bottom-0 left-1/2 w-px -translate-x-1/2 bg-gradient-to-b from-transparent via-gray-500/25 to-transparent" />
              </div>
            ) : (
              <div className={`relative w-full ${sheetCls}`}>
                <div className="absolute -left-1.5 top-1 bottom-1 w-3 rounded-l-lg bg-gradient-to-b from-gray-300 via-gray-200 to-gray-300 shadow-[inset_0_0_4px_rgba(0,0,0,0.18)]" />
                <div className="absolute -right-1.5 top-1 bottom-1 w-3 rounded-r-lg bg-gradient-to-b from-gray-300 via-gray-200 to-gray-300 shadow-[inset_0_0_4px_rgba(0,0,0,0.18)]" />
                <div className="bg-white rounded-lg overflow-hidden shadow-lg min-h-[240px] flex items-center justify-center">
                  <canvas ref={singleRef} className="bv-canvas" />
                </div>
              </div>
            )}

            {/* tap zones (prev/next) */}
            <div className="absolute inset-x-0 bottom-0 top-0 pointer-events-none">
              <div
                className="absolute left-0 top-0 bottom-0 w-1/4 pointer-events-auto cursor-pointer"
                onClick={() => go(-1)}
                aria-label="Sebelumnya"
              />
              <div
                className="absolute right-0 top-0 bottom-0 w-1/4 pointer-events-auto cursor-pointer"
                onClick={() => go(1)}
                aria-label="Berikutnya"
              />
            </div>
          </div>
        </div>

        {/* hint */}
        <div className="text-center text-[10px] text-gray-400 pb-2 flex-shrink-0 select-none">
          Geser kiri/kanan • +/− untuk zoom
        </div>
      </div>
    </div>
  );
}
